/**
 * @fileoverview Public portfolio page — shareable, no auth required.
 *
 * Displays master overview (instruments, instructors, schools, venues),
 * instrument filtering, AI portfolio content, and basic stats.
 */

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Music, BookOpen, Calendar, X,
  Sparkles, Zap, Brain, Star, Award, User, School, MapPin, GraduationCap,
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { companyName } from "@/lib/companyBrand";

const LEVEL_COLORS = {
  beginner: "bg-zinc-700 text-zinc-300",
  developing: "bg-blue-900/50 text-blue-300",
  proficient: "bg-emerald-900/50 text-emerald-300",
  advanced: "bg-purple-900/50 text-purple-300",
  mastered: "bg-[#f4cf08]/20 text-[#f4cf08]",
};

const STATUS_COLORS = {
  introduced: "bg-zinc-700 text-zinc-300",
  learning: "bg-blue-900/50 text-blue-300",
  practicing: "bg-blue-900/50 text-blue-300",
  proficient: "bg-emerald-900/50 text-emerald-300",
  solid: "bg-emerald-900/50 text-emerald-300",
  mastered: "bg-[#f4cf08]/20 text-[#f4cf08]",
};

const CATEGORY_ICONS = {
  technique: Zap,
  theory: Brain,
  ear_training: Music,
  performance: Star,
  repertoire: BookOpen,
};

const PublicPortfolioPage = () => {
  // Two doors, one page (F8). A student's chosen handle is an ADDITIONAL
  // URL — the account-id link keeps working forever, because links already
  // in the world must not break when somebody claims a prettier one.
  const { accountId, handle } = useParams();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { exportPortfolioPdf } = await import("@/lib/exportPortfolioPdf");
      await exportPortfolioPdf(portfolio);
    } catch (_err) {
      // A failed export must not blank the page somebody came here to read.
      setExporting(false);
      return;
    }
    setExporting(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const path = handle ? `/student/portfolio/h/${handle}` : `/student/portfolio/${accountId}`;
        const res = await axios.get(`${API}${path}`);
        setPortfolio(res.data);
      } catch (_err) {
        setError("Portfolio not found");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [accountId, handle]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatDuration = (seconds) => {
    const s = Math.max(0, Math.round(Number(seconds) || 0));
    if (s < 60) return `${s} sec`;
    const m = Math.floor(s / 60);
    return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}m`;
  };

  const filterByInstrument = (items) => {
    if (!selectedInstrument) return items || [];
    return (items || []).filter((item) => item.instrument === selectedInstrument || !item.instrument);
  };

  if (loading) {
    return (
      <div
        className="min-h-screen bg-[#09090b] flex items-center justify-center" // brand-lint-ignore: dark-only public page; this pre-existing literal is pinned by the data-theme attribute on this tag
        data-theme="dark"
      >
        <div className="w-12 h-12 border-4 border-[#f4cf08] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !portfolio) {
    return (
      <div
        className="min-h-screen bg-[#09090b] flex items-center justify-center text-center p-8" // brand-lint-ignore: dark-only public page; this pre-existing literal is pinned by the data-theme attribute on this tag
        data-theme="dark"
      >
        <div>
          <GraduationCap className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h1 className="text-xl text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Portfolio Not Found</h1>
          <p className="text-zinc-400 text-sm">This portfolio may have been removed or the link is incorrect.</p>
        </div>
      </div>
    );
  }

  const instrumentStats = portfolio.instrument_stats || {};
  const manualAdditions = portfolio.manual_additions || {};
  const sortedInstruments = Object.entries(instrumentStats)
    .sort(([, a], [, b]) => b.count - a.count)
    .map(([name, stats]) => ({ name, ...stats, manual: false }));
  (manualAdditions.instruments || []).forEach((mi) => {
    if (!sortedInstruments.find((i) => i.name === mi.name)) {
      sortedInstruments.push({ name: mi.name, count: 0, last_date: null, manual: true });
    }
  });

  return (
    <div
      className="min-h-screen bg-[#09090b]" // brand-lint-ignore: dark-only public page; this pre-existing literal is pinned by the data-theme attribute on this tag
      data-theme="dark"
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1800px] h-[1200px]" style={{
          background: "radial-gradient(ellipse at center, rgba(244,207,8,0.05) 0%, rgba(244,207,8,0.01) 40%, rgba(244,207,8,0) 70%)",
        }} />
      </div>

      <nav className="relative z-10 flex items-center justify-between px-4 md:px-8 py-4 md:py-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-3">
          <img src="/logo192.png" alt="Hans" className="h-8 rounded-lg" />
          <span className="text-sm font-semibold text-white tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>Hans</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-sans text-xs text-[var(--text-tertiary)]">Student Portfolio</span>
          {/* The reason the export exists: a document a student can attach to
              a music-school application. */}
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            data-testid="portfolio-export"
            className="rounded-full border border-[var(--studio-divider-strong)] px-3 py-1.5 font-sans text-[12px] text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)] disabled:opacity-50"
          >
            {exporting ? "Preparing…" : "Download PDF"}
          </button>
        </div>
      </nav>

      <main className="relative z-10 px-4 md:px-8 py-8 max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-9 h-9 text-[#f4cf08]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-light text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              {portfolio.name}
            </h1>
            {portfolio.member_since && (
              <p className="text-zinc-500 text-xs mt-2">Student since {formatDate(portfolio.member_since)}</p>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5 text-center">
              <BookOpen className="w-5 h-5 text-zinc-500 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-2xl font-light text-white">{portfolio.total_lessons}</p>
              <p className="text-xs text-zinc-500 mt-1">Lessons</p>
            </div>
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5 text-center">
              <Calendar className="w-5 h-5 text-zinc-500 mx-auto mb-2" strokeWidth={1.5} />
              <p className="text-2xl font-light text-white">{portfolio.total_practice_sessions}</p>
              <p className="text-xs text-zinc-500 mt-1">Sessions</p>
            </div>
          </div>

          {/* ─── Master Overview ─── */}

          {/* Instruments */}
          {sortedInstruments.length > 0 && (
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5 mb-4">
              <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Music className="w-4 h-4 text-[#f4cf08]" /> Instruments
              </h2>
              <div className="flex gap-2 overflow-x-auto pb-1">
                <button
                  onClick={() => setSelectedInstrument(null)}
                  className={`shrink-0 px-4 py-2 rounded-xl text-sm border transition-colors ${
                    !selectedInstrument ? "bg-[#f4cf08]/10 border-[#f4cf08]/30 text-[#f4cf08]" : "bg-zinc-800/50 border-white/5 text-zinc-400 hover:text-white"
                  }`}
                >All</button>
                {sortedInstruments.map((inst) => (
                  <button
                    key={inst.name}
                    onClick={() => setSelectedInstrument(inst.name === selectedInstrument ? null : inst.name)}
                    className={`shrink-0 px-4 py-2 rounded-xl text-sm transition-colors ${inst.manual ? "border-dashed" : "border-solid"} border ${
                      selectedInstrument === inst.name
                        ? "bg-[#f4cf08]/10 border-[#f4cf08]/30 text-[#f4cf08]"
                        : inst.manual ? "bg-zinc-800/30 border-zinc-600 text-zinc-500" : "bg-zinc-800/50 border-white/5 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {inst.name}
                    {inst.count > 0 && <span className="ml-1.5 text-[10px] text-zinc-500">{inst.count} lessons</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Instructors */}
          {((portfolio.teachers || []).length > 0 || (manualAdditions.instructors || []).length > 0) && (
            <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5 mb-4">
              <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <User className="w-4 h-4 text-[#f4cf08]" /> Instructors
              </h2>
              <div className="flex flex-wrap gap-2">
                {(portfolio.teachers || []).map((t) => (
                  <div key={t.user_id} className="bg-zinc-800/50 border border-white/5 rounded-xl px-4 py-2.5 flex items-center gap-2">
                    {t.picture ? <img src={t.picture} alt="" className="w-6 h-6 rounded-full" /> : (
                      <div className="w-6 h-6 rounded-full bg-zinc-700 flex items-center justify-center text-[10px] text-zinc-400">{t.name?.[0]}</div>
                    )}
                    <span className="text-sm text-white">{t.name}</span>
                  </div>
                ))}
                {(manualAdditions.instructors || []).map((mi, i) => (
                  <div key={`m-${i}`} className="bg-zinc-800/30 border border-dashed border-zinc-600 rounded-xl px-4 py-2.5 flex items-center gap-2">
                    <User className="w-4 h-4 text-zinc-500" />
                    <span className="text-sm text-zinc-400">{mi.name}</span>
                    {mi.instrument && <span className="text-[10px] text-zinc-600">({mi.instrument})</span>}
                    {mi.years && <span className="text-[10px] text-zinc-600">{mi.years}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Schools & Venues */}
          {((manualAdditions.schools || []).length > 0 || (manualAdditions.venues || []).length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {(manualAdditions.schools || []).length > 0 && (
                <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
                  <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <School className="w-4 h-4 text-[#f4cf08]" /> Schools & Camps
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {manualAdditions.schools.map((s, i) => (
                      <div key={i} className="bg-zinc-800/30 border border-dashed border-zinc-600 rounded-xl px-3 py-2">
                        <p className="text-sm text-zinc-400">{s.name}</p>
                        {s.program && <p className="text-[10px] text-zinc-600">{s.program}</p>}
                        {s.years && <p className="text-[10px] text-zinc-600">{s.years}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(manualAdditions.venues || []).length > 0 && (
                <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
                  <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#f4cf08]" /> Performance Venues
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {manualAdditions.venues.map((v, i) => (
                      <div key={i} className="bg-zinc-800/30 border border-dashed border-zinc-600 rounded-xl px-3 py-2">
                        <p className="text-sm text-zinc-400">{v.name}</p>
                        {v.event && <p className="text-[10px] text-zinc-600">{v.event}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Instrument filter indicator */}
          {selectedInstrument && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-zinc-500">Filtered by:</span>
              <span className="bg-[#f4cf08]/10 text-[#f4cf08] text-xs px-3 py-1 rounded-full flex items-center gap-1">
                <Music className="w-3 h-3" /> {selectedInstrument}
                <button onClick={() => setSelectedInstrument(null)} className="ml-1"><X className="w-3 h-3" /></button>
              </span>
            </div>
          )}

          {/* Practice + achieved goals (F8). Both are already-collected facts
              rather than a new class of disclosure: practice totals have
              always been on this page, and an achieved goal is the only part
              of it that is ground truth — a teacher looked at the work and
              said it was done. Each is withheld entirely when the student has
              turned its section off. */}
          {portfolio.practice && (portfolio.practice.longest_streak_days > 0 || portfolio.practice.total_rehearsals > 0) ? (
            <div className="mb-4 grid grid-cols-3 gap-3" data-testid="portfolio-practice">
              {[
                ["Current streak", `${portfolio.practice.current_streak_days}d`],
                ["Best streak", `${portfolio.practice.longest_streak_days}d`],
                ["Rehearsals", portfolio.practice.total_rehearsals],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-4 text-center">
                  <p className="font-mono text-xl text-[var(--text-primary)]">{value}</p>
                  <p className="mt-1 font-sans text-[11px] text-[var(--text-tertiary)]">{label}</p>
                </div>
              ))}
            </div>
          ) : null}

          {portfolio.goals?.length > 0 ? (
            <div className="mb-6 rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-5" data-testid="portfolio-goals">
              <h2 className="mb-3 font-sans text-sm font-medium text-[var(--text-primary)]">Goals achieved</h2>
              <ul className="flex flex-col gap-2">
                {portfolio.goals.map((g, i) => (
                  <li key={i} className="font-sans text-[14px] text-[var(--text-secondary)]">
                    {g.title}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {/* AI Portfolio Content */}
          {portfolio.ai_portfolio ? (
            <AIPortfolioDisplay ai={portfolio.ai_portfolio} filterFn={filterByInstrument} />
          ) : (
            portfolio.recent_lessons?.length > 0 && (
              <div className="mb-10">
                <h2 className="text-lg font-medium text-white mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Lesson Timeline</h2>
                <div className="space-y-2">
                  {portfolio.recent_lessons.map((lesson, i) => (
                    <div key={i} className="bg-zinc-900/50 border border-white/5 rounded-2xl p-4 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">{lesson.title}</p>
                        <p className="text-zinc-500 text-xs mt-1">
                          {formatDate(lesson.created_at)}
                          {lesson.duration_seconds ? ` · ${formatDuration(lesson.duration_seconds)}` : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          )}
        </motion.div>
      </main>

      <footer className="relative z-10 border-t border-white/5 px-4 md:px-8 py-6 mt-10">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/logo192.png" alt="Hans" className="w-5 h-5 rounded" />
            <span className="text-zinc-500 text-xs">Powered by Hans</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-zinc-500 text-xs hover:text-white transition-colors">Are you a music teacher? Try Hans free</Link>
            <span className="text-zinc-700 text-xs">by {companyName()} &copy; {new Date().getFullYear()}</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

/* ─── AI Portfolio Display (with instrument filtering) ─── */

const AIPortfolioDisplay = ({ ai, filterFn }) => {
  const skills = filterFn(ai.skills);
  const repertoire = filterFn(ai.repertoire);
  const concepts = filterFn(ai.core_concepts);
  const milestones = filterFn(ai.milestones);
  const { summary, highlights } = ai;

  const skillsByCategory = {};
  skills.forEach((skill) => {
    const cat = skill.category || "other";
    if (!skillsByCategory[cat]) skillsByCategory[cat] = [];
    skillsByCategory[cat].push(skill);
  });

  return (
    <div className="space-y-6 mb-10">
      {summary && (
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-[#f4cf08]" /> Musical Journey
          </h2>
          <p className="text-zinc-300 text-sm leading-relaxed">{summary}</p>
        </div>
      )}

      {highlights?.length > 0 && (
        <div className="bg-[#f4cf08]/5 border border-[#f4cf08]/10 rounded-2xl p-5">
          <h2 className="text-sm font-medium text-[#f4cf08] mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Highlights
          </h2>
          <ul className="space-y-2">
            {highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-200">
                <Star className="w-3.5 h-3.5 text-[#f4cf08] mt-0.5 shrink-0" /> {h}
              </li>
            ))}
          </ul>
        </div>
      )}

      {skills.length > 0 && (
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <h2 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#f4cf08]" /> Skills
          </h2>
          <div className="space-y-4">
            {Object.entries(skillsByCategory).map(([category, catSkills]) => {
              const Icon = CATEGORY_ICONS[category] || Zap;
              return (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className="w-3.5 h-3.5 text-zinc-500" />
                    <span className="text-xs text-zinc-500 uppercase tracking-wider">{category.replace("_", " ")}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {catSkills.map((skill, i) => (
                      <div key={i} className="bg-zinc-800/50 border border-white/5 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-white">{skill.name}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${LEVEL_COLORS[skill.level] || LEVEL_COLORS.beginner}`}>{skill.level}</span>
                        </div>
                        {skill.detail && <p className="text-xs text-zinc-500">{skill.detail}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {repertoire.length > 0 && (
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
            <Music className="w-4 h-4 text-[#f4cf08]" /> Repertoire
          </h2>
          <div className="space-y-2">
            {repertoire.map((piece, i) => (
              <div key={i} className="flex items-center justify-between bg-zinc-800/50 border border-white/5 rounded-xl p-3">
                <div>
                  <p className="text-sm text-white">{piece.title}</p>
                  {piece.composer && <p className="text-xs text-zinc-500">{piece.composer}</p>}
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[piece.status] || STATUS_COLORS.introduced}`}>{piece.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {concepts.length > 0 && (
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
            <Brain className="w-4 h-4 text-[#f4cf08]" /> Core Concepts
          </h2>
          <div className="space-y-2">
            {concepts.map((concept, i) => (
              <div key={i} className="bg-zinc-800/50 border border-white/5 rounded-xl p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-white">{concept.name}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLORS[concept.mastery] || STATUS_COLORS.introduced}`}>{concept.mastery}</span>
                </div>
                <p className="text-xs text-zinc-500">{concept.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {milestones.length > 0 && (
        <div className="bg-zinc-900/50 border border-white/5 rounded-2xl p-5">
          <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
            <Award className="w-4 h-4 text-[#f4cf08]" /> Milestones
          </h2>
          <div className="space-y-3">
            {milestones.map((m, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-2 h-2 bg-[#f4cf08] rounded-full mt-1.5" />
                  {i < milestones.length - 1 && <div className="w-px flex-1 bg-zinc-800 mt-1" />}
                </div>
                <div className="pb-3">
                  <p className="text-sm text-white">{m.title}</p>
                  <p className="text-xs text-zinc-500">{m.description}</p>
                  {m.date && <p className="text-xs text-zinc-600 mt-1">{m.date}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicPortfolioPage;
